import { FC, useEffect, useRef } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Search from '@/storybook/components/Search/Search';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import type { ChatbotActiveFilters, ChatbotMessage } from '@/models/chatbot';
import useChatHistoryPanel from '../hooks/useChatHistoryPanel';
import ChatHistoryStatus from './ChatHistoryStatus';
import ChatHistoryList from './ChatHistoryList';
import RenameConversationDialog from './RenameConversationDialog';
import DeleteConversationDialog from './DeleteConversationDialog';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './ChatHistoryPanel.module.css';

interface ChatHistoryPanelProps {
  isOpen: boolean;
  onConversationLoaded: (
    conversationId: string,
    messages: ChatbotMessage[],
    pinnedFilters: ChatbotActiveFilters | null,
  ) => void;
}

const ChatHistoryPanel: FC<ChatHistoryPanelProps> = ({ isOpen, onConversationLoaded }) => {
  const { t } = useTranslation();
  const {
    activeConversationId,
    searchTerm,
    setSearchTerm,
    clearSearch,
    isSearchDisabled,
    isLoading,
    isError,
    loadErrorMessage,
    openError,
    isLoadMoreError,
    loadMoreErrorMessage,
    retryLoad,
    retryLoadMore,
    dismissLoadError,
    dismissOpenError,
    showEmptyState,
    emptyTitle,
    emptySubtitle,
    groups,
    timeLabels,
    openingId,
    renamedId,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    openConversation,
    retryOpen,
    startRenaming,
    handleRenameAnimationEnd,
    renameTarget,
    renameError,
    handleRenameSave,
    handleRenameCancel,
    clearRenameError,
    isRenamePending,
    deleteTarget,
    deleteConversationTitle,
    setDeleteTarget,
    handleDeleteCancel,
    handleDeleteConfirm,
    isDeletePending,
  } = useChatHistoryPanel({ isOpen, onConversationLoaded });

  const panelRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      panelRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className={cx(styles.panel, { [styles.open]: isOpen })}
      aria-hidden={!isOpen}
      inert={!isOpen}
      data-testid={CHAT_HISTORY_TEST_IDS.panel}
    >
      <div className={styles.content}>
        <div className={styles.searchRow}>
          <Search
            size="L"
            className={styles.search}
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder={t('components.aiChatDrawer.history.searchPlaceholder')}
            width="100%"
            disabled={isSearchDisabled}
            autoComplete="off"
            data-testid={CHAT_HISTORY_TEST_IDS.search}
          />
        </div>

        <ScrollArea
          scrollbars="y"
          viewportRef={scrollViewportRef}
          className={styles.scrollArea}
          data-testid={CHAT_HISTORY_TEST_IDS.list}
        >
          <div className={styles.bodyInner}>
            <ChatHistoryStatus
              isLoading={isLoading}
              isError={isError}
              loadErrorMessage={loadErrorMessage}
              openError={openError}
              showEmptyState={showEmptyState}
              emptyTitle={emptyTitle}
              emptySubtitle={emptySubtitle}
              onRetryLoad={retryLoad}
              onRetryOpen={retryOpen}
              onDismissLoad={dismissLoadError}
              onDismissOpen={dismissOpenError}
            />

            <ChatHistoryList
              groups={groups}
              timeLabels={timeLabels}
              openingId={openingId}
              highlight={searchTerm}
              selectedId={activeConversationId}
              onOpen={openConversation}
              onDelete={setDeleteTarget}
              pagination={{
                hasNextPage,
                isFetchingNextPage,
                onReachEnd: fetchNextPage,
                isError: isLoadMoreError,
                errorMessage: loadMoreErrorMessage,
                onRetry: retryLoadMore,
              }}
              rename={{ renamedId, onStart: startRenaming, onAnimationEnd: handleRenameAnimationEnd }}
              scrollRootRef={scrollViewportRef}
            />
          </div>
        </ScrollArea>
      </div>

      <RenameConversationDialog
        isOpen={renameTarget !== null}
        isLoading={isRenamePending}
        value={renameTarget?.title ?? ''}
        errorMessage={renameError}
        onSave={handleRenameSave}
        onCancel={handleRenameCancel}
        onClearError={clearRenameError}
      />

      <DeleteConversationDialog
        isOpen={deleteTarget !== null}
        isLoading={isDeletePending}
        conversationTitle={deleteConversationTitle}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default ChatHistoryPanel;
